import { Button, Group, Stack } from "@mantine/core";
import { useQueries } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, TextSerif } from "@/components/elements";
import { getGarageCarsQueryOptions } from "@/services/cars/get-garage-cars";
import { useGarages } from "@/services/garages/get-garages";

import type { FC } from "react";

export type CarComparatorProps = {
  defaultCar1Id?: string;
  defaultCar2Id?: string;
  defaultCar3Id?: string;
  onCancel?: () => void;
  onCompare: (car1Id: string, car2Id: string, car3Id?: string) => void;
  garageId: string | undefined;
  carsData?: { label: string; value: string }[];
};

export const CarComparator: FC<CarComparatorProps> = ({
  defaultCar1Id,
  defaultCar2Id,
  defaultCar3Id,
  onCancel,
  onCompare,
  garageId,
  carsData,
}) => {
  const { t } = useTranslation();
  const [car1Id, setCar1Id] = useState<string | null | undefined>(defaultCar1Id || null);
  const [car2Id, setCar2Id] = useState<string | null | undefined>(defaultCar2Id || null);
  const [car3Id, setCar3Id] = useState<string | null | undefined>(defaultCar3Id || null);

  const { data: garages } = useGarages();
  const garageCars = useQueries({
    queries: (garages?.results ?? []).map((garage) =>
      getGarageCarsQueryOptions({ garageId: garage.identifier, page: 1, pageSize: 999999 })
    ),
  });
  const accountCars = garageCars.flatMap((query) => query.data?.results ?? []);
  const optionsById = new Map(
    accountCars.map((car) => [
      car.identifier,
      {
        label: `${car.make} ${car.model}${car.nickname ? ` (${car.nickname})` : ""}`,
        value: car.identifier,
      },
    ])
  );
  (carsData ?? []).forEach((car) => {
    if (!optionsById.has(car.value)) optionsById.set(car.value, car);
  });
  const data = [...optionsById.values()];

  function handleSubmit() {
    if (car1Id && car2Id) {
      onCompare(car1Id, car2Id, car3Id || undefined);
    }
  }

  function handleCar1Change(value?: string | null) {
    if (value) {
      setCar1Id(value);
      return;
    }

    setCar1Id(car2Id || null);
    setCar2Id(car3Id || null);
    setCar3Id(null);
  }

  function handleCar2Change(value?: string | null) {
    if (value) {
      setCar2Id(value);
      return;
    }

    setCar2Id(car3Id || null);
    setCar3Id(null);
  }

  if (!garageId && data.length === 0 && garageCars.length === 0) {
    return null;
  }

  return (
    <Stack>
      <TextSerif>{t("compare.comparator.title")}</TextSerif>
      <Dropdown
        data={data}
        value={car1Id}
        onChange={handleCar1Change}
        label={t("compare.comparator.label1")}
        placeholder={t("compare.comparator.placeholder")}
        searchable
      />
      <Dropdown
        data={data}
        value={car2Id}
        onChange={handleCar2Change}
        label={t("compare.comparator.label2")}
        placeholder={t("compare.comparator.placeholder")}
        searchable
      />
      <Dropdown
        data={data}
        value={car3Id}
        disabled={!car2Id}
        onChange={setCar3Id}
        label={t("compare.comparator.label3")}
        placeholder={t("compare.comparator.placeholder")}
        searchable
      />

      <Group gap="md" pt="md">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} flex={1} aria-label={t("common.cancel")}>
            {t("common.cancel")}
          </Button>
        )}
        <Button
          disabled={!car1Id || !car2Id}
          onClick={handleSubmit}
          flex={1}
          aria-label={t("compare.comparator.submit")}
        >
          {t("compare.comparator.submit")}
        </Button>
      </Group>
    </Stack>
  );
};
